import BigNumber from 'bignumber.js';
import html2canvas from 'html2canvas';
import moment from 'moment';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { ThemeContext } from '../../../theme/ThemeProvider';
import { useFetcher, useFetcherPublic } from '../../hooks/fetcher';
import { useExchangeStore } from '../../hooks/trade/exchangeStore';
import { useIndexPrice } from '../../hooks/trade/indexprice';
import CheckBox from '@/app/components/CheckBox';
import {
  BottomSection,
  ButtonSection,
  CancelBtn,
  CharacterImage,
  CheckboxLabel,
  CheckboxSection,
  CheckboxText,
  ContentWrapper,
  CustomModal,
  DownloadBtn,
  InfoAndQrSection,
  InfoItem,
  InfoLabel,
  InfoValue,
  LogoSection,
  LongShortBadge,
  ModalContainer,
  PairInfoSection,
  PairName,
  PlusIcon,
  PnlSection,
  PnlValue,
  PriceInfoSection,
  QRContainer,
  QrSection,
  ReferralCode,
  ReferralInfo,
  ReferralLabel,
  RoiDisplay,
  RoiValue,
} from './styles';
import { useSystemSetting } from '@/app/hooks/system/GetSystemSetting';
import { VITE_APP_API_MEDIA_URL } from '@/app/constants/APIKEYS';
import CharacterImageSrc from './assets/character.png';
import LogoImageSrc from './assets/logo.png';
import CBottomSheet from '@/app/components/Common/CBottomSheet';

function useImageDownload(backgroundColor: string, isDarkMode: boolean) {
  const captureArea = useRef<HTMLDivElement>(null);

  async function getLocalImageUrl(url: string) {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return URL.createObjectURL(blob); // blob://xxxx 형태
  }
  /* 다운로드 버튼 함수 */
  const onImageDownload = async (systemImage: string) => {
    if (!captureArea.current) return;

    if (systemImage) {
      const localUrl = await getLocalImageUrl(
        `${VITE_APP_API_MEDIA_URL}/${systemImage}`
      );

      const backgroundImgElement = new Image();
      backgroundImgElement.src = localUrl;

      backgroundImgElement.onload = async () => {
        captureArea.current!.style.backgroundImage = `url(${localUrl})`;
        captureArea.current!.style.backgroundSize = 'cover';
        captureArea.current!.style.backgroundPosition = 'center';
        captureArea.current!.style.backgroundRepeat = 'no-repeat';
        captureArea.current!.style.padding = '40px';
        captureArea.current!.style.backgroundColor = 'transparent';

        //안전하게 한 프레임 대기
        await new Promise((r) => requestAnimationFrame(r));

        const canvas = await html2canvas(captureArea.current!, {
          scale: 4,
          backgroundColor: backgroundColor,
          useCORS: true,
        });
        captureArea.current!.style.backgroundImage = '';
        captureArea.current!.style.padding = '0px';
        const element = document.createElement('a');
        element.href = canvas.toDataURL('image/png');
        element.download = 'SHARE.png';
        element.click();
      };
    } else {
      captureArea.current.style.padding = '40px';

      const canvas = await html2canvas(captureArea.current, {
        scale: 4,
        backgroundColor: backgroundColor,
      });
      captureArea.current.style.padding = '0px';
      const element = document.createElement('a');
      element.href = canvas.toDataURL('image/png');
      element.download = 'SHARE.png';
      element.click();
    }
  };

  // 이미지 blob 형태로 가져오기, 이부분은 아직 미완성, 필요할때 완성할 예정
  const getImageBlob = async () => {
    if (captureArea.current) {
      const canvas = await html2canvas(captureArea.current);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        return url;
      });
    }
  };

  return {
    captureArea,
    onImageDownload,
    getImageBlob,
  };
}

const ShareModal = ({
  shareData,
  setShareData,
  type = 1,
  isMobile = false,
}) => {
  const { isDarkMode, theme } = useContext(ThemeContext);
  const { EPs } = useExchangeStore();
  const [lastprice, setLastPrice] = React.useState(0);

  const [showpnl, setShowpnl] = React.useState(true);
  const [showRate, setShowRate] = React.useState(true);
  const [showprice, setShowprice] = React.useState(true);
  const [showreferral, setShowreferral] = React.useState(true);
  const { t } = useTranslation();

  const { captureArea, onImageDownload, getImageBlob } = useImageDownload(
    theme.COLORS.PrimaryBg,
    isDarkMode
  );

  const { SystemSetting: backgroundImage } = useSystemSetting('CHIWEB'); // 시스템에 설정되어 있는 백그라운드 이미지지
  const systemImage =
    backgroundImage?.result === 'undefined' ? null : backgroundImage?.result;

  const { getPrice } = useIndexPrice(shareData?.exchange_pairs_id);
  const { data } = useFetcher('/referral/code', false);
  const currencyList = useFetcherPublic('/system/currency');

  const equivusd = currencyList?.data?.result?.result?.find(
    (item: any) => item?.code === 'KRW'
  );

  const backSvgColor = isDarkMode ? '#252525' : '#F8F9FB';

  useEffect(() => {
    if (!shareData) return;
    if (type == 1) {
      const price = getPrice(shareData?.exchange_pairs_id);
      setLastPrice(price);
      if (!price) {
        setTimeout(() => {
          setLastPrice(getPrice(shareData?.exchange_pairs_id));
        }, 1000);
      }
    } else {
      // type=2일 때는 종료된 포지션의 평균 진입가 계산
      const openOrders =
        shareData?.orders?.filter((order: any) => order.open_close === 'O') ||
        [];
      let avgEntryPrice = 0;

      if (openOrders.length > 0) {
        let totalAmount = 0;
        let weightedSum = 0;
        openOrders.forEach((order: any) => {
          const amount = Number(order.amount || 0);
          const price = Number(order.trade_price || 0);
          totalAmount += amount;
          weightedSum += amount * price;
        });
        avgEntryPrice = totalAmount > 0 ? weightedSum / totalAmount : 0;
      }

      setLastPrice(
        avgEntryPrice && !isNaN(Number(avgEntryPrice))
          ? Number(avgEntryPrice)
          : 0
      );
    }
    setShowpnl(true);
    setShowRate(true);
    setShowprice(true);
    setShowreferral(true);
  }, [shareData]);

  if (!shareData) {
    return null;
  }
  const referralCode = data?.result?.result?.code || '';

  const EP = EPs.find((ep) => ep.id === shareData?.exchange_pairs_id);

  let pnl = 0;
  let pnlRate = 0;

  if (type == 1) {
    // 현재 포지션의 PnL 계산 시 유효성 검증
    const entryPrice = shareData?.price; // 현재 포지션의 진입가
    const amount = shareData?.amount;
    const leverage = shareData?.leverage;
    const margin = shareData?.margin;

    if (
      entryPrice &&
      !isNaN(Number(entryPrice)) &&
      amount &&
      !isNaN(Number(amount)) &&
      leverage &&
      !isNaN(Number(leverage)) &&
      lastprice &&
      !isNaN(Number(lastprice))
    ) {
      if (shareData?.long_short == 'S') {
        pnl = BigNumber(BigNumber(entryPrice).minus(lastprice))
          .multipliedBy(amount)
          .multipliedBy(leverage)
          .toNumber();
      } else {
        pnl = BigNumber(BigNumber(lastprice).minus(entryPrice))
          .multipliedBy(amount)
          .multipliedBy(leverage)
          .toNumber();
      }

      if (margin && !isNaN(Number(margin)) && Number(margin) > 0) {
        pnlRate = BigNumber(pnl).dividedBy(margin).multipliedBy(100).toNumber();
      }
    }
  } else {
    // 종료된 포지션의 PnL 계산 시 유효성 검증
    const pnlPrice = shareData?.pnl_price;
    const margin = shareData?.margin;

    if (pnlPrice && !isNaN(Number(pnlPrice))) {
      pnl = Number(pnlPrice);

      if (margin && !isNaN(Number(margin)) && Number(margin) > 0) {
        pnlRate = new BigNumber(pnlPrice)
          .dividedBy(margin)
          .multipliedBy(100)
          .toNumber();
      }
    }
  }

  // 가중평균 청산가 계산 함수
  const getAvgExitPrice = () => {
    const closeOrders =
      shareData?.orders?.filter((order: any) => order.open_close === 'C') || [];
    let avgExitPrice = 0;

    if (closeOrders.length > 0) {
      let totalAmount = 0;
      let weightedSum = 0;
      closeOrders.forEach((order: any) => {
        const amount = Number(order.amount || 0);
        const price = Number(order.trade_price || 0);
        totalAmount += amount;
        weightedSum += amount * price;
      });
      avgExitPrice = totalAmount > 0 ? weightedSum / totalAmount : 0;
    }

    return avgExitPrice && !isNaN(Number(avgExitPrice))
      ? new BigNumber(avgExitPrice).toFormat(EP?.dp || 3)
      : '0.000';
  };

  // 공통 컨텐츠
  const renderContent = (isMobile = false) => (
    <ModalContainer $isMobile={isMobile}>
      <ContentWrapper ref={captureArea}>
        {/* Logo Section */}
        <LogoSection>
          <img alt="LOGO" src={LogoImageSrc} />
        </LogoSection>

        {/* Pair Info Section */}
        <PairInfoSection>
          <PairName>{EP?.pairs}</PairName>
          <LongShortBadge isLong={shareData?.long_short !== 'S'}>
            {shareData?.long_short === 'S' ? 'Short' : 'Long'}{' '}
            {shareData?.leverage}x
          </LongShortBadge>
        </PairInfoSection>

        {/* PnL Section */}
        <PnlSection>
          {showRate && (
            <RoiDisplay isPositive={pnl >= 0}>
              {pnl >= 0 && <PlusIcon isPositive={pnl >= 0}>+</PlusIcon>}
              <RoiValue isPositive={pnl >= 0}>{pnlRate.toFixed(2)}%</RoiValue>
            </RoiDisplay>
          )}
          {showpnl && (
            <PnlValue isPositive={pnl >= 0}>
              {new BigNumber(pnl).toFormat(EP?.dp || 4)} USDT
            </PnlValue>
          )}
        </PnlSection>

        {/* Price Info and QR Section */}
        <InfoAndQrSection>
          <PriceInfoSection>
            {showprice && (
              <>
                <InfoItem>
                  <InfoLabel>{t('entryPrice')}</InfoLabel>
                  <InfoValue>
                    {(() => {
                      if (type == 1) {
                        const price = shareData?.price;
                        return price && !isNaN(Number(price))
                          ? new BigNumber(price).toFormat(EP?.dp || 3)
                          : '0.000';
                      } else {
                        return lastprice && !isNaN(Number(lastprice))
                          ? new BigNumber(lastprice).toFormat(EP?.dp || 3)
                          : '0.000';
                      }
                    })()}
                  </InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>
                    {type == 1 ? t('markPrice') : t('exitPrice')}
                  </InfoLabel>
                  <InfoValue>
                    {(() => {
                      if (type == 1) {
                        return lastprice && !isNaN(Number(lastprice))
                          ? new BigNumber(lastprice).toFormat(EP?.dp || 3)
                          : '0.000';
                      } else {
                        return getAvgExitPrice();
                      }
                    })()}
                  </InfoValue>
                </InfoItem>
              </>
            )}

            <InfoItem>
              <InfoLabel>{t(type == 1 ? 'entryTime' : 'endDate')}</InfoLabel>
              <InfoValue>
                {moment
                  .utc(
                    type == 1
                      ? shareData?.created_at
                      : shareData?.closed_at ||
                          shareData?.updated_at ||
                          shareData?.created_at
                  )
                  .utcOffset(9)
                  .format('YYYY-MM-DD HH:mm:ss')}
              </InfoValue>
            </InfoItem>
          </PriceInfoSection>

          {showreferral && (
            <QrSection>
              <CharacterImage>
                <img
                  src={CharacterImageSrc}
                  alt="character"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </CharacterImage>
              <QRContainer>
                <QRCode
                  size={256}
                  style={{
                    height: 'auto',
                    maxWidth: 80,
                    width: 80,
                  }}
                  value={`${window.location.origin}/signup?ref=${referralCode}`}
                  viewBox={`0 0 80 80`}
                />
              </QRContainer>
              <ReferralInfo>
                <ReferralLabel>{t('referralCode')}</ReferralLabel>
                <ReferralCode>{referralCode}</ReferralCode>
              </ReferralInfo>
            </QrSection>
          )}
        </InfoAndQrSection>
      </ContentWrapper>

      <BottomSection>
        <CheckboxSection>
          <CheckboxLabel>
            <CheckBox checked={showpnl} setChecked={setShowpnl} />
            <CheckboxText>{t('Profit')}</CheckboxText>
          </CheckboxLabel>
          <CheckboxLabel>
            <CheckBox checked={showRate} setChecked={setShowRate} />
            <CheckboxText>{t('ROI')}</CheckboxText>
          </CheckboxLabel>
          <CheckboxLabel>
            <CheckBox checked={showprice} setChecked={setShowprice} />
            <CheckboxText>{t('price')}</CheckboxText>
          </CheckboxLabel>
        </CheckboxSection>

        <ButtonSection>
          <CancelBtn onClick={() => setShareData(null)}>{t('CLOSE')}</CancelBtn>
          <DownloadBtn
            onClick={() => {
              onImageDownload(systemImage);
            }}
          >
            {t('imagedownload')}
          </DownloadBtn>
        </ButtonSection>
      </BottomSection>
    </ModalContainer>
  );

  // 모바일: CBottomSheet, 데스크톱: Modal
  if (isMobile) {
    return (
      <CBottomSheet
        isOpen={shareData ? true : false}
        onClose={() => setShareData(null)}
        height="auto"
      >
        {renderContent(true)}
      </CBottomSheet>
    );
  }

  return (
    <CustomModal
      style={{ position: 'relative' }}
      open={shareData ? true : false}
      footer={null}
      onCancel={() => {
        setShareData(null);
      }}
      closable={false}
      systemImage={systemImage}
      centered
    >
      {renderContent()}
    </CustomModal>
  );
};
export default React.memo(ShareModal);
